import { type Node } from '@xyflow/react';
import { ComparatorUnits, CompareAsUnits } from 'automations/data/comapreNode';
import { waitUnits } from 'automations/data/waitNode';
import { z } from 'zod';

import { ConditionTypes } from '../automationConfig';
import { CommonNodeDataSchema } from './node';

export const ConditionNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.nativeEnum(ConditionTypes),
});

export const IfElseNodeDataSchema = ConditionNodeDataSchema.extend({
  nodeSubType: z.literal(ConditionTypes.ifElse),
  additionalOptions: z.object({
    criteriaGroupId: z.number({
      required_error: 'Comparison Condition is required',
    }),
  }).optional().refine((data) => data !== undefined, {
    message: 'Comparison Condition is required',
  }),
});

export const WaitNodeDataSchema = ConditionNodeDataSchema.extend({
  nodeSubType: z.literal(ConditionTypes.wait),
  additionalOptions: z.object({
    wait: z.discriminatedUnion('type', [
      z.object({
        type: z.literal('custom'),
        custom: z.string(),
      }),
      z.object({
        type: z.literal('for'),
        for: z.object({
          offset: z.union([z.number().int(), z.string()]),
          unit: z.enum(waitUnits),
        }),
      }),
    ]),
  }),
});

export const CompareNodeDataSchema = ConditionNodeDataSchema.extend({
  nodeSubType: z.literal(ConditionTypes.compare),
  additionalOptions: z.object({
    compare: z.object({
      comparator: z.enum(['and', 'or'], {
        required_error: 'Comparator is required',
      }),
      criterias: z.array(z.object({
        conditionKey: z.string().min(1, 'Valid LHS value is required for one or more conditions'),
        comparator: z.nativeEnum(ComparatorUnits, {
          message: 'Comparator is required for one or more conditions',
        }),
        conditionValue: z.string().optional(),
        compareAs: z.nativeEnum(CompareAsUnits, {
          message: 'Compare As is required for one or more conditions',
        }),
      })).min(1, 'At least one condition is required'),
    }),
  }).optional().refine((data) => data !== undefined, {
    message: 'At least one Compare criteria must be added for node',
  }),
});

export const MultiIfElseNodeDataSchema = ConditionNodeDataSchema.extend({
  nodeSubType: z.literal(ConditionTypes.multiIfElse),
  additionalOptions: z.object({
    criteriaGroupId: z.number().optional(),
  }).optional(),
});

export const IfNodeDataSchema = ConditionNodeDataSchema.extend({
  nodeSubType: z.literal(ConditionTypes.if),
  additionalOptions: z.object({
    criteriaGroupId: z.number({
      required_error: 'Comparison Condition is required',
    }),
  }).optional().refine((data) => data !== undefined, {
    message: 'Comparison Condition is required',
  }),
});

export const ElseNodeDataSchema = ConditionNodeDataSchema.extend({
  nodeSubType: z.literal(ConditionTypes.else),
});

export type ConditionNodeData = z.infer<typeof ConditionNodeDataSchema>;
export type IfElseNodeData = z.infer<typeof IfElseNodeDataSchema>;
export type WaitNodeData = z.infer<typeof WaitNodeDataSchema>;
export type CompareNodeData = z.infer<typeof CompareNodeDataSchema>;
export type MultiIfElseNodeData = z.infer<typeof MultiIfElseNodeDataSchema>;
export type IfNodeData = z.infer<typeof IfNodeDataSchema>;
export type ElseNodeData = z.infer<typeof ElseNodeDataSchema>;

export type ConditionNodeType = Node<ConditionNodeData>;
export type IfElseNodeType = Node<IfElseNodeData>;
export type WaitNodeType = Node<WaitNodeData>;
export type CompareNodeType = Node<CompareNodeData>;
export type MultiIfElseNodeType = Node<MultiIfElseNodeData>;
export type IfNodeType = Node<IfNodeData>;
export type ElseNodeType = Node<ElseNodeData>;
